import {useMemo} from "react";
import {Dialog, DialogContent, useMediaQuery} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {MembershipQueueItem} from "../../api/payment";
import "./styles.scss";

interface TimelineItem {
    pill: string;
    pillClass: 'max' | 'pro' | 'plus' | 'free';
    time: string;
    sub: string;
    subRed?: boolean;
    pillRight?: boolean;
    active?: boolean;
    current?: boolean;
}

const TIER_CLASS: Record<string, TimelineItem['pillClass']> = {
    max: 'max',
    pro: 'pro',
    plus: 'plus',
    free: 'free',
};

const TIER_NAME: Record<string, string> = {
    max: 'Max',
    pro: 'Pro',
    plus: 'Plus',
};

const formatTime = (value?: string | Date | null) => {
    if (!value) return '--';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return typeof value === 'string' ? value : '--';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${day} ${hh}:${mm}`;
};

const tierKey = (tier: string) => tier.replace('_year', '');
const tierClassOf = (tier: string): TimelineItem['pillClass'] => TIER_CLASS[tierKey(tier)] ?? 'plus';
const tierNameOf = (tier: string) => TIER_NAME[tierKey(tier)] ?? tierKey(tier).toUpperCase();

// 友好文案：tierDescription 若含「会员」视为友好直接用；否则按 tier 拼（如 plus → 月度Plus会员）
const tierLabelOf = (tier: string, tierDescription?: string) => {
    if (tierDescription && tierDescription.includes('会员')) return tierDescription;
    const key = tierKey(tier);
    if (key === 'free') return '免费会员';
    const isYear = tier.includes('_year');
    return `${isYear ? '年度' : '月度'}${tierNameOf(tier)}会员`;
};

// 加 N 个月（月长不足时 clamp 到月末），保留时分
const addMonthsToDate = (date: Date, months: number): Date => {
    const d = new Date(date);
    const day = d.getDate();
    d.setMonth(d.getMonth() + months);
    if (d.getDate() !== day) d.setDate(0);
    return d;
};

// 该条会员的月数：年会员 12 个月，月会员取 duration_months 或 1
const durationMonthsOf = (item: MembershipQueueItem): number =>
    item.tier?.includes('_year') ? 12 : (item.duration_months || 1);

const buildTimeline = (items: MembershipQueueItem[], baseTime?: string | null): TimelineItem[] => {
    // 过期/已取消的不展示；保持数组原始顺序
    const baseList = items.filter(item => item.status !== 'expired' && item.status !== 'cancelled');
    // 只保留第一个 active（当前生效），后续 active 不展示
    let activeKept = false;
    const valid = baseList.filter(item => {
        if (item.status === 'active') {
            if (activeKept) return false;
            activeKept = true;
        }
        return true;
    });

    if (!valid.length) return [];

    const entries: TimelineItem[] = [];
    // 基准：当前会员到期时间（与 ExistingMembershipDialog 的 calculateEndDateFromQueue 一致）
    let cursor: Date | null = baseTime ? new Date(baseTime) : null;
    let lastBase = '';
    let currentSeen = false;

    valid.forEach((item) => {
        const base = tierLabelOf(item.tier, item.tierDescription);
        lastBase = base;

        // 当前生效：只认第一个 active 状态的条目
        const isCurrent = item.status === 'active' && !currentSeen;
        if (isCurrent) currentSeen = true;

        // 生效时间：有激活时间用激活时间，否则用上一条到期（从基准 currentMemberEndTime 起）
        const activation = item.activated_at
            ? new Date(item.activated_at)
            : (cursor ?? new Date(item.created_at));

        // 到期时间：当前生效的锚定为基准（currentMemberEndTime），queued 用 生效+时长（年/月）
        const expiry = isCurrent
            ? (cursor ?? addMonthsToDate(activation, durationMonthsOf(item)))
            : addMonthsToDate(activation, durationMonthsOf(item));

        // 当前生效中的标签直接用会员名（如「月度Plus会员」），queued 保留「生效」
        entries.push({
            pill: isCurrent ? base : `${base}生效`,
            pillClass: tierClassOf(item.tier),
            time: formatTime(activation),
            sub: isCurrent ? `${base}会员水滴发放` : `${base}水滴发放`,
            current: isCurrent,
            active: isCurrent,
            pillRight: entries.length % 2 === 1,
        });

        cursor = expiry;
    });

    // 最后一条的到期时间 → 红色到期条目
    if (cursor) {
        entries.push({
            pill: 'Free',
            pillClass: 'free',
            time: formatTime(cursor),
            sub: `${lastBase}到期`,
            subRed: true,
            pillRight: entries.length % 2 === 1,
        });
    }

    return entries;
};

export default function MemberIdentityDialog(props: {open: boolean; onClose: () => void; membershipQueue?: MembershipQueueItem[]; currentMemberEndTime?: string | null}) {
    const {open, onClose, membershipQueue, currentMemberEndTime} = props;
    const isMobile = useMediaQuery('(max-width: 768px)');

    const timeline = useMemo(
        () => buildTimeline(membershipQueue ?? [], currentMemberEndTime),
        [membershipQueue, currentMemberEndTime]
    );

    const renderPill = (item: TimelineItem) => (
        <div className={`mid-pill mid-pill--${item.pillClass} mid-pill--arrow-${item.pillRight ? 'left' : 'right'}`}>{item.pill}</div>
    );

    const renderTime = (item: TimelineItem) => {
        if (item.current) {
            return (
                <div className="mid-time">
                    <div className="mid-time-main">当前生效会员</div>
                </div>
            );
        }
        return (
            <div className="mid-time">
                <div className="mid-time-main">{item.time}</div>
                <div className={`mid-time-sub${item.subRed ? ' is-red' : ''}`}>{item.sub}</div>
            </div>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={isMobile}
            maxWidth="none"
            aria-labelledby="member-identity-dialog-title"
            sx={{
                '& .MuiDialog-paper': {
                    width: isMobile ? '100%' : 1034,
                    maxWidth: '100vw',
                    borderRadius: isMobile ? '0' : '12px',
                    boxShadow: isMobile ? 'none' : '0 4px 16px var(--c-shadow-card)',
                },
                '& .MuiDialogContent-root': {
                    padding: 0,
                },
            }}
        >
            <DialogContent>
                <div className="mid">
                    <div className="mid-header">
                        <div className="mid-title" id="member-identity-dialog-title">会员身份详情</div>
                        <div className="mid-close" onClick={onClose}><CloseIcon/></div>
                    </div>

                    <div className="mid-timeline">
                        {timeline.length === 0 ? (
                            <div className="mid-empty">暂无会员记录</div>
                        ) : (
                            <div className="mid-timeline-content">
                                <div className="mid-line"/>
                                {timeline.map((item, idx) => (
                                    <div className="mid-entry" key={idx}>
                                        <div className="mid-slot">
                                            {item.pillRight ? renderTime(item) : renderPill(item)}
                                        </div>
                                        <div className={`mid-dot${item.active ? ' is-active' : ''}`}/>
                                        <div className="mid-slot">
                                            {item.pillRight ? renderPill(item) : renderTime(item)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="mid-note">注：会员水滴可跨周期累积，会员权益全部到期后失效</div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
